import React from "react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { colors } from "src/config/colors";
import Image from "next/image";
import SubmitButton from "src/components/common/Buttons/SubmitButton";
import { pluralPharmaciesAmount } from "src/utils/helpers/pluralization/pluralPharmaciesAmount";
import { useRouter } from "next/router";
import { ResponsiveStyleValue } from "@mui/system";
import { Property } from "csstype";
import Link from "src/components/common/Links/Link";

interface ProductCardParams {
  advertisement?: boolean;
  name?: string;
  company?: string;
  country?: string;
  offers_amount: number;
  min_price?: number;
  image: string;
  slug?: string;
  full_slug: string;
  isLoadingAnalogs: boolean;
  minWidth?: ResponsiveStyleValue<Property.MinWidth<string | number>>;
  padding?: string;
}

const ProductCard: React.FC<ProductCardParams> = ({
  advertisement,
  company,
  country,
  full_slug,
  slug,
  image,
  name,
  min_price,
  offers_amount,
  isLoadingAnalogs,
  minWidth = "232px",
  padding = "10px 12px",
}) => {
  const router = useRouter();

  return (
    <Box
      sx={{
        padding: padding,
      }}
    >
      <Paper
        sx={{
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #EBEBEB",
          minWidth: minWidth,
          maxWidth: "232px",
          minHeight: "370px",
          margin: 0,
          position: "relative",
          boxShadow: "none",
          ":hover": {
            boxShadow: "0px 3px 16px 0px rgba(12, 21, 57, 0.1)",
          },
        }}
      >
        <Box>
          {advertisement && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                left: 0,
                borderRadius: "0px 4px 4px 0px",
                fontSize: "12px",
                backgroundColor: "#D9821C",
                padding: "2px 10px",
                color: colors.white,
                zIndex: 10,
              }}
            >
              Реклама
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoadingAnalogs ? (
              <Skeleton width="100%" height="110px" />
            ) : (
              <Box
                component={Link}
                href={full_slug}
                sx={{
                  width: { xs: "136px", sm: "200px" },
                  height: { xs: "136px", sm: "200px" },
                }}
              >
                <Image
                  alt="изображение препарата"
                  src={image}
                  width="100%"
                  height="100%"
                  layout="responsive"
                />
              </Box>
            )}
          </Box>
          {isLoadingAnalogs ? (
            <Skeleton width="100%" height="50px" />
          ) : (
            <Link href={full_slug} sx={{ textDecoration: "none" }}>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: "14px", tb: "16px" },
                  fontWeight: 500,
                  color: colors.black2,
                  mt: "8px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  lineHeight: "20px",
                }}
              >
                {name}
              </Typography>
            </Link>
          )}
          {/*<Typography
            component="div"
            sx={{
              fontSize: "14px",
              color: colors.black2,
              mb: "8px",
            }}
          >
            {company}, {country}
          </Typography>*/}
        </Box>
        <Box>
          {isLoadingAnalogs ? (
            <Skeleton width="100%" height="30px" />
          ) : (
            <Typography
              component="div"
              sx={{
                fontSize: { xs: "12px", tb: "14px" },
                color: colors.gray3,
                mb: "16px",
              }}
            >
              {offers_amount &&
                `В наличии в ${offers_amount}
              ${pluralPharmaciesAmount(offers_amount)}`}
            </Typography>
          )}
          <Box
            sx={{
              display: min_price && min_price > 0 ? "flex" : "none",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: { xs: "16px", tb: "20px" },
            }}
          >
            {isLoadingAnalogs ? (
              <Skeleton width="100%" height="30px" />
            ) : (
              <>
                <Typography
                  component="span"
                  sx={{
                    color: colors.gray3,
                    fontSize: "16px",
                  }}
                >
                  Цена
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    color: colors.blue2,
                    fontSize: { xs: "18px", tb: "24px" },
                    fontWeight: 500,
                  }}
                >
                  от {min_price} ₽
                </Typography>
              </>
            )}
          </Box>
          {isLoadingAnalogs ? (
            <Skeleton width="100%" height="50px" />
          ) : (
            <SubmitButton
              onClick={() => router.push(full_slug)}
              title={min_price && min_price > 0 ? "Подробнее" : "Нет в наличии"}
              disabled={(min_price && min_price <= 0) || !min_price}
              maxWidth={200}
              height={48}
              sx={{
                borderRadius: "8px",
                minWidth: "100%",
              }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductCard;
